import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { BrandMark } from "../components/Brand";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Custom Google OAuth — backend computes the redirect URI from the inbound
// Host header. Frontend just hits /api/auth/google/start; backend handles
// the rest.
function googleSigninUrl() {
  const next = window.location.pathname.startsWith("/signin") || window.location.pathname.startsWith("/login")
    ? "/dashboard"
    : window.location.pathname;
  return `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start?next=${encodeURIComponent(next)}`;
}

export default function SignIn() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const reasonParam = params.get("reason");
  const errorParam = params.get("error");
  const reauthParam = params.get("reauth");
  const initialError =
    errorParam === "oauth_failed"
      ? "Google sign-in failed. Try again."
      : reasonParam === "session_expired"
      ? "Your session has ended. Please sign in again."
      : reasonParam === "refresh_failed"
      ? "Sign-in didn't complete. Please try again."
      : "";
  const [error, setError] = useState(initialError);
  const [collisionEmail, setCollisionEmail] = useState(null);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCollisionEmail(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      await loginWithToken(data.access_token);
      if (data.needs_onboarding) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Inverse collision: this email is registered via Google only.
      // Surface the dedicated "Continue with Google" CTA inline, NOT a
      // generic error toast.
      if (detail && typeof detail === "object" && detail.error === "email_registered_with_google") {
        setCollisionEmail(email);
        setError("");
      } else {
        setError(typeof detail === "string" ? detail : "Sign in failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50" data-testid="signin-page">
      <header className="border-b border-ink-200 bg-white">
        <div className="hp-container flex h-16 items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/signup" className="hp-btn-ghost" data-testid="signin-to-signup">
            Need an account? Sign up
          </Link>
        </div>
      </header>
      <main className="flex items-start justify-center px-6 py-16">
        <div className="w-full max-w-[420px]">
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink-900">
            Sign in to Hiretape
          </h1>
          <p className="mt-2 text-[14px] text-ink-500">
            Continue with your workspace.
          </p>

          {reauthParam === "1" && (
            <div
              className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900"
              data-testid="signin-reauth-banner"
            >
              <strong>We&rsquo;ve updated how Google sign-in works.</strong> Please sign in again to continue — all your data is intact and ready when you&rsquo;re back.
            </div>
          )}

          <button
            type="button"
            onClick={() => (window.location.href = googleSigninUrl())}
            className="hp-btn-outline mt-8 w-full"
            data-testid="signin-google-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.1A6.999 6.999 0 015.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A10.997 10.997 0 001 12c0 1.78.43 3.45 1.18 4.93l3.66-2.83z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-[12px] text-ink-400">
            <div className="hp-divider flex-1" />
            <span className="uppercase tracking-wider">or with email</span>
            <div className="hp-divider flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signin-form">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Email
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                className="hp-input mt-1.5"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signin-email"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Password
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                className="hp-input mt-1.5"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signin-password"
              />
            </div>
            {collisionEmail && (
              <div
                className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900 space-y-2"
                data-testid="signin-collision-banner"
              >
                <div>
                  <strong>{collisionEmail}</strong> is registered with Google sign-in.
                </div>
                <button
                  type="button"
                  onClick={() => (window.location.href = googleSigninUrl())}
                  className="hp-btn-primary w-full"
                  data-testid="signin-collision-google-cta"
                >
                  Continue with Google
                </button>
              </div>
            )}
            {error && (
              <div
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700"
                data-testid="signin-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={1.5} />
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="hp-btn-primary w-full"
              data-testid="signin-submit"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
              Sign in
            </button>
          </form>

          <p className="mt-6 text-center text-[13px] text-ink-500">
            No account yet?{" "}
            <Link to="/signup" className="hp-link" data-testid="signin-link-signup">
              Create one
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
